import React, { useState, useRef, useEffect } from 'react'
import { CompactPicker } from 'react-color'
import styles from './FixedToolbar.module.scss'

const FONT_OPTIONS = [
  'Arial',
  'Avenir Next LT Pro',
  'AvenirNextLTPro',
  'Barcode',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
]

/**
 * FixedToolbar - A horizontal toolbar fixed at the top of the canvas panel.
 * Used in customer mode for text formatting controls.
 */
export default function FixedToolbar({
  field,
  selectedCount = 0,
  onUpdate,
  fonts = [],
  disabled = false,
}) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showFontDropdown, setShowFontDropdown] = useState(false)
  const [showSpacingPopup, setShowSpacingPopup] = useState(false)
  const [spacingInput, setSpacingInput] = useState('')
  const [isEditingSpacing, setIsEditingSpacing] = useState(false)
  const [fontSizeInput, setFontSizeInput] = useState('')
  const [isEditingFontSize, setIsEditingFontSize] = useState(false)
  const colorPickerRef = useRef(null)
  const fontDropdownRef = useRef(null)
  const spacingPopupRef = useRef(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target)) {
        setShowFontDropdown(false)
      }
      if (spacingPopupRef.current && !spacingPopupRef.current.contains(e.target)) {
        setShowSpacingPopup(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fontOptions = fonts.length > 0
    ? fonts.map(f => ({ label: f.name, value: f.id }))
    : FONT_OPTIONS.map(f => ({ label: f, value: f }))

  const currentFontName = field?.fontName || 'Arial'
  const currentFontSize = field?.textSize || 16

  const handleFontChange = (fontName, fontId) => {
    onUpdate({ fontName, fontId })
    setShowFontDropdown(false)
  }

  const handleFontSizeChange = (delta) => {
    const newSize = Math.max(8, Math.min(200, currentFontSize + delta))
    onUpdate({ textSize: newSize })
  }

  const handleFontSizeFocus = () => {
    setIsEditingFontSize(true)
    setFontSizeInput(currentFontSize.toString())
  }

  const handleFontSizeBlur = () => {
    setIsEditingFontSize(false)
    const value = parseInt(fontSizeInput, 10)
    if (!isNaN(value) && value >= 8 && value <= 200) {
      onUpdate({ textSize: value })
    }
    setFontSizeInput('')
  }

  const handleFontSizeKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur()
    else if (e.key === 'Escape') {
      setIsEditingFontSize(false)
      setFontSizeInput('')
    }
  }

  const handleColorChange = (color) => {
    onUpdate({ color: color.hex })
  }

  const handleBoldToggle = () => {
    const currentStyle = field?.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')
    let newStyle = 'normal'
    if (!isBold && isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'bold'
    else if (isBold && isItalic) newStyle = 'italic'
    onUpdate({ fontStyle: newStyle })
  }

  const handleItalicToggle = () => {
    const currentStyle = field?.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')
    let newStyle = 'normal'
    if (isBold && !isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'italic'
    else if (isBold && isItalic) newStyle = 'bold'
    onUpdate({ fontStyle: newStyle })
  }

  const handleUnderlineToggle = () => {
    const currentDecoration = field?.textDecoration || ''
    if (currentDecoration.includes('underline')) {
      onUpdate({ textDecoration: currentDecoration.replace('underline', '').trim() })
    } else {
      onUpdate({ textDecoration: (currentDecoration + ' underline').trim() })
    }
  }

  const handleAlignChange = (align) => {
    onUpdate({ align })
  }

  const handleVerticalAlignChange = (verticalAlign) => {
    onUpdate({ verticalAlign })
  }

  const currentSpacing = field?.spacing || 0

  const handleSpacingSliderChange = (e) => {
    const value = parseInt(e.target.value, 10)
    onUpdate({ spacing: value })
  }

  const handleSpacingInputFocus = () => {
    setIsEditingSpacing(true)
    setSpacingInput(currentSpacing.toString())
  }

  const handleSpacingInputChange = (e) => {
    setSpacingInput(e.target.value)
  }

  const handleSpacingInputBlur = () => {
    setIsEditingSpacing(false)
    const value = parseInt(spacingInput, 10)
    if (!isNaN(value) && value >= -10 && value <= 40) {
      onUpdate({ spacing: value })
    }
    setSpacingInput('')
  }

  const handleSpacingInputKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur()
    else if (e.key === 'Escape') {
      setIsEditingSpacing(false)
      setSpacingInput('')
    }
  }

  const handleSpacingReset = () => {
    onUpdate({ spacing: 0 })
  }

  // Prevent clicks on toolbar from propagating
  const handleToolbarClick = (e) => {
    e.stopPropagation()
  }

  return (
    <div className={styles.fixedToolbar} onClick={handleToolbarClick}>
      {/* Font selector */}
      <div className={styles.fontSelector} ref={fontDropdownRef}>
        <button
          type="button"
          className={styles.fontButton}
          onClick={() => !disabled && setShowFontDropdown(!showFontDropdown)}
          disabled={disabled}
        >
          <span className={styles.fontName}>{disabled ? 'Font' : currentFontName}</span>
          <span className={styles.dropdownArrow}>▼</span>
        </button>
        {showFontDropdown && !disabled && (
          <div className={styles.fontDropdown}>
            {fontOptions.map((font) => (
              <div
                key={font.value}
                className={styles.fontOption}
                style={{ fontFamily: font.label }}
                onClick={() => handleFontChange(font.label, font.value)}
              >
                {font.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Font size */}
      <div className={styles.fontSizeControl}>
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(-1)}
          disabled={disabled}
        >
          −
        </button>
        <input
          type="text"
          className={styles.sizeInput}
          value={isEditingFontSize ? fontSizeInput : (disabled ? '—' : currentFontSize)}
          onChange={(e) => setFontSizeInput(e.target.value)}
          onFocus={handleFontSizeFocus}
          onBlur={handleFontSizeBlur}
          onKeyDown={handleFontSizeKeyDown}
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(1)}
          disabled={disabled}
        >
          +
        </button>
      </div>

      <div className={styles.divider} />

      {/* Bold */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.fontStyle || '').includes('bold') ? styles.active : ''}`}
        title="Bold"
        onClick={handleBoldToggle}
        disabled={disabled}
      >
        <strong>B</strong>
      </button>

      {/* Italic */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.fontStyle || '').includes('italic') ? styles.active : ''}`}
        title="Italic"
        onClick={handleItalicToggle}
        disabled={disabled}
      >
        <em>I</em>
      </button>

      {/* Underline */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.textDecoration || '').includes('underline') ? styles.active : ''}`}
        title="Underline"
        onClick={handleUnderlineToggle}
        disabled={disabled}
      >
        <span style={{ textDecoration: 'underline' }}>U</span>
      </button>

      <div className={styles.divider} />

      {/* Alignment */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'left' ? styles.active : ''}`}
        onClick={() => handleAlignChange('left')}
        title="Align left"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM2 6h8v1H2zM2 9h12v1H2zM2 12h8v1H2z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'center' ? styles.active : ''}`}
        onClick={() => handleAlignChange('center')}
        title="Align center"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM4 6h8v1H4zM2 9h12v1H2zM4 12h8v1H4z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'right' ? styles.active : ''}`}
        onClick={() => handleAlignChange('right')}
        title="Align right"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM6 6h8v1H6zM2 9h12v1H2zM6 12h8v1H6z"/>
        </svg>
      </button>

      <div className={styles.divider} />

      {/* Vertical Alignment */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.verticalAlign === 'top' ? styles.active : ''}`}
        onClick={() => handleVerticalAlignChange('top')}
        title="Align top"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 2h12v1H2zM5 5h6v1H5zM5 8h6v1H5z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (!field?.verticalAlign || field?.verticalAlign === 'middle') ? styles.active : ''}`}
        onClick={() => handleVerticalAlignChange('middle')}
        title="Align middle"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M5 4h6v1H5zM2 7.5h12v1H2zM5 11h6v1H5z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.verticalAlign === 'bottom' ? styles.active : ''}`}
        onClick={() => handleVerticalAlignChange('bottom')}
        title="Align bottom"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M5 7h6v1H5zM5 10h6v1H5zM2 13h12v1H2z"/>
        </svg>
      </button>

      <div className={styles.divider} />

      {/* Letter spacing */}
      <div className={styles.spacingControl || styles.colorControl} ref={spacingPopupRef}>
        <button
          type="button"
          className={styles.formatButton}
          onClick={() => !disabled && setShowSpacingPopup(!showSpacingPopup)}
          title="Letter spacing"
          disabled={disabled}
        >
          <svg width="20" height="16" viewBox="0 0 20 16">
            <text x="2" y="12" fontSize="11" fontWeight="bold" fill="currentColor">A</text>
            <text x="11" y="12" fontSize="11" fontWeight="bold" fill="currentColor">V</text>
            <path d="M4 14h12" stroke="currentColor" strokeWidth="1" strokeDasharray="1,1.5" />
          </svg>
        </button>
        {showSpacingPopup && !disabled && (
          <div className={styles.colorPickerPopup} style={{ padding: '8px', minWidth: '200px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Letter spacing</span>
              <button
                type="button"
                onClick={handleSpacingReset}
                style={{ fontSize: '10px', cursor: 'pointer', border: 'none', background: 'none', color: '#4a90d9' }}
              >
                Reset
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="range"
                min="-10"
                max="40"
                step="1"
                value={currentSpacing}
                onChange={handleSpacingSliderChange}
                style={{ flex: 1 }}
              />
              <input
                type="text"
                value={isEditingSpacing ? spacingInput : currentSpacing}
                onChange={handleSpacingInputChange}
                onFocus={handleSpacingInputFocus}
                onBlur={handleSpacingInputBlur}
                onKeyDown={handleSpacingInputKeyDown}
                style={{ width: '36px', textAlign: 'center', fontSize: '11px', border: '1px solid #ccc', borderRadius: '3px', padding: '2px' }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      {/* Text color */}
      <div className={styles.colorControl} ref={colorPickerRef}>
        <button
          type="button"
          className={styles.colorButton}
          onClick={() => !disabled && setShowColorPicker(!showColorPicker)}
          title="Text color"
          disabled={disabled}
        >
          <span className={styles.colorIcon}>A</span>
          <span
            className={styles.colorBar}
            style={{ backgroundColor: disabled ? '#999' : (field?.color || '#000000') }}
          />
        </button>
        {showColorPicker && !disabled && (
          <div className={styles.colorPickerPopup}>
            <CompactPicker
              color={field?.color || '#000000'}
              onChange={handleColorChange}
            />
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {selectedCount > 1 && (
        <span className={styles.selectionCount}>{selectedCount} selected</span>
      )}
    </div>
  )
}
